<template>
  <div class="h-roll">
    <div class="h-roll__content">
      <div class="h-roll__head">
        <div>
          <h1 class="h-roll__title">Roles</h1>
          <p class="h-roll__sub">{{ subtitle }}</p>
        </div>
        <div v-if="platform" class="h-roll__actions">
          <button type="button" class="h-roll__new" @click="openNew">
            <svg viewBox="0 0 24 24" class="h-roll__newi"><path d="M12 5v14M5 12h14"/></svg>
            New role
          </button>
        </div>
      </div>

      <p v-if="!loading && !error && !platform" class="h-roll__note">
        Roles are shared by every vendor, so only a platform operator can change them.
      </p>

      <HermesRoleEditorDrawer
        v-model="editorOpen"
        :api-base="apiBase"
        :role="editing"
        :groups="groups"
        :platform="platform"
        @saved="load"
      />

      <HermesEmptyState
        v-if="error"
        variant="error"
        title="Couldn't load roles"
        :message="error"
        @retry="load"
      />
      <HermesEmptyState
        v-else-if="!loading && !rows.length"
        variant="first-run"
        title="No roles yet"
        message="A role is a named set of permissions that you give to users."
      />
      <div v-else class="h-roll__card">
        <HermesTabulator
          :rows="rows"
          :columns="columns"
          :loading="loading"
          storage-key="admin-roles"
          :initial-sort="{ column: 'name', dir: 'asc' }"
          @row-click="openEdit"
        />
      </div>
      <p v-if="!loading && rows.length" class="h-roll__hint">Select a row to see what the role allows.</p>
    </div>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

// HermesRolesList — roles and the permissions they carry. Hermes's own screen:
// io-user 6 removed /role/list and ships nothing in its place. A HermesTabulator
// over GET /admin/roles; a row opens HermesRoleEditorDrawer.
//
// Roles are global (one "Vendor Administrator" role serves every vendor), so only
// a platform operator may change one. Everyone else gets the same screen read-only.
export default {
  name: 'HermesRolesList',
  props: {
    apiBase: { type: String, default: '/api/v1' },
    componentData: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      api: useApi(),
      rows: [],
      groups: [],
      platform: false,
      loading: true,
      error: null,
      editorOpen: false,
      editing: null,
    };
  },
  computed: {
    subtitle() {
      if (this.loading) return 'Loading…';
      const n = this.rows.length;
      return `${n} role${n === 1 ? '' : 's'}`;
    },
    columns() {
      return [
        {
          key: 'name', label: 'Role',
          format: (v, row) => {
            const tag = row.built_in ? ' <span class="h-roll__tag">built in</span>' : '';
            return `${this.esc(v)}${tag}`;
          },
        },
        { key: 'permission_count', label: 'Permissions', width: 150, sorter: 'number', headerFilter: false },
        { key: 'user_count', label: 'Users', width: 120, sorter: 'number', headerFilter: false },
        { key: 'created', label: 'Created', width: 140, sorter: 'number', headerFilter: false, format: (v) => this.date(v) },
      ];
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const data = await this.api.get(`${this.apiBase}/admin/roles`);
        this.groups = data.meta?.permissions || [];
        this.platform = !!data.meta?.platform;
        this.rows = (data.items || []).map((r) => ({
          ...r,
          permission_count: (r.permissions || []).length + (r.other_permissions || []).length,
        }));
      } catch (e) {
        const status = e?.response?.status;
        this.error = `Failed to load roles${status ? ` (${status})` : ''}. ${e?.response?.data?.message || e?.message || ''}`.trim();
      } finally {
        this.loading = false;
      }
    },
    openNew() {
      this.editing = null;
      this.editorOpen = true;
    },
    openEdit(row) {
      this.editing = row;
      this.editorOpen = true;
    },
    esc(v) {
      return String(v ?? '').replace(/[<>&"]/g, (ch) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[ch]));
    },
    date(v) {
      if (!v) return '<span style="color:var(--h-text-tertiary)">—</span>';
      const d = new Date(Number(v) * 1000);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.h-roll { font-family: var(--h-font-body, system-ui); color: var(--h-text-primary, #0F172A); background: var(--h-bg-content, #F8FAFC); min-height: 100vh; }
.h-roll__content { padding: 26px 32px 64px; max-width: 1440px; width: 100%; }
.h-roll__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; margin-bottom: 22px; }
.h-roll__title { font-size: 24px; font-weight: 600; letter-spacing: -0.02em; margin: 0 0 4px; }
.h-roll__sub { font-size: 14px; color: var(--h-text-secondary, #64748B); margin: 0; }
.h-roll__note { font-size: 13px; color: var(--h-text-secondary, #64748B); background: var(--h-bg-subtle, #F1F5F9); border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius, 6px); padding: 10px 13px; margin: 0 0 16px; }
.h-roll__card { background: var(--h-bg-card, #fff); border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius-lg, 10px); box-shadow: var(--h-shadow-card); overflow: hidden; }
.h-roll__hint { font-size: 12.5px; color: var(--h-text-tertiary, #94A3B8); margin: 12px 2px 0; }
.h-roll__new { font: inherit; font-size: 13.5px; font-weight: 500; padding: 9px 15px; border-radius: var(--h-radius, 6px); border: 1px solid transparent; background: var(--h-accent, #0EA5E9); color: #fff; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; }
.h-roll__new:hover { background: var(--h-accent-hover, #0284C7); }
.h-roll__new:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }
.h-roll__newi { width: 15px; height: 15px; stroke: currentColor; fill: none; stroke-width: 2.4; stroke-linecap: round; }
.h-roll :deep(.tabulator-row) { cursor: pointer; }
.h-roll :deep(.h-roll__tag) { display: inline-block; margin-left: 6px; padding: 1px 7px; border-radius: 999px; font-size: 11px; font-weight: 600; background: var(--h-bg-subtle, #F1F5F9); color: var(--h-text-secondary, #64748B); }
</style>
